import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { integrationsRoutes } from 'constants/navigation';
import {
  CoderbyteWorkspaceStatus,
  useCoderbyteWorkspaceQuery,
  useProvisionCoderbyteWorkspaceMutation,
} from 'generated/graphql';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './IntegrationsTab.module.scss';

const dataTestId = 'integrations-coderbyte';

type CoderbyteCardProps = {
  companyId: number;
};

const statusLabels: Record<CoderbyteWorkspaceStatus, string> = {
  [CoderbyteWorkspaceStatus.PendingSetup]: 'Awaiting setup',
  [CoderbyteWorkspaceStatus.Active]: 'Active',
  [CoderbyteWorkspaceStatus.Suspended]: 'Suspended',
};

/**
 * Whether this company has coding assessments, and a way through to managing
 * them.
 *
 * Deliberately thin. Enabling Coderbyte is a decision about this company and
 * belongs here beside its other integrations, but the workspace's credentials
 * are Spotted Zebra's own operational secrets rather than company settings —
 * those live under Integrations, away from the pages a customer's setup gets
 * discussed on.
 */
export function CoderbyteCard({ companyId }: CoderbyteCardProps) {
  const navigate = useNavigate();

  const { data, loading, refetch } = useCoderbyteWorkspaceQuery({
    variables: { companyId },
    fetchPolicy: 'cache-and-network',
  });

  const workspace = data?.coderbyteWorkspace ?? null;

  return (
    <div className={styles.viewContainer} data-testid={dataTestId}>
      <div className={styles.header}>
        <h3 className={styles.title}>Coderbyte</h3>
        {/* Navigated rather than wrapped in a <Link>: `Button` renders a real
            <button>, and a button inside an anchor is invalid markup that
            swallows the click instead of following the link. */}
        {workspace && (
          <Button
            variant="secondary"
            size="medium"
            onClick={() =>
              navigate(integrationsRoutes.coderbyteWorkspace.url(companyId))
            }
          >
            Manage workspace
          </Button>
        )}
      </div>

      {/* Silent while loading so the "not connected" copy never flashes on
          first paint — same behaviour as the ATS cards above. */}
      {!loading && !workspace && (
        <NotProvisioned companyId={companyId} onProvisioned={() => refetch()} />
      )}

      {workspace && (
        <div className={styles.content}>
          <InfoItem
            label="Status"
            value={statusLabels[workspace.status]}
            valueNote={
              workspace.status === CoderbyteWorkspaceStatus.PendingSetup
                ? 'Needs an admin password and an API key before it can be used.'
                : undefined
            }
          />
          <InfoItem
            label="Organization ID"
            value={workspace.externalId}
            hasCopyButton
          />
        </div>
      )}
    </div>
  );
}

type NotProvisionedProps = {
  companyId: number;
  onProvisioned: () => void;
};

/**
 * Provisioning is behind a confirmation because it creates a real, billable
 * Coderbyte organization and permanently consumes an admin address that can
 * never be reclaimed for this company.
 *
 * That is true in every environment, not just production. Non-production
 * environments used to share one organization between every company, which
 * made this button free to press outside production; they provision for real
 * now, so the warning has to say so — the only thing holding the bill down is
 * `CODERBYTE_ALLOWED_COMPANY_IDS` on the server.
 */
function NotProvisioned({ companyId, onProvisioned }: NotProvisionedProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const { handleMsgType } = useNotification();

  const [provision, { loading }] = useProvisionCoderbyteWorkspaceMutation({
    variables: { companyId },
    onCompleted: () => {
      handleMsgType({
        type: TNotification.success,
        title: 'Coderbyte workspace created.',
        message: 'Finish setting it up under Integrations.',
        dismissable: true,
      });
      setIsConfirming(false);
      onProvisioned();
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        title: 'Could not create the Coderbyte workspace',
        message: error.message,
        dismissable: true,
      });
    },
  });

  return (
    <div className={styles.content}>
      <p className={styles.description}>
        This company has no Coderbyte workspace. Creating one gives it its own
        Coderbyte account, which then has to be set up under Integrations before
        anyone can use it.
      </p>

      {isConfirming ? (
        <div className={styles.resultCard}>
          <p className={styles.resultCardTitle}>
            Create a Coderbyte workspace for this company?
          </p>
          <p className={styles.caption}>
            This creates a billable Coderbyte account in every environment, this
            one included, and permanently uses up this company&apos;s admin
            email address. Coderbyte cannot release it afterwards.
          </p>
          <div className={styles.buttonRow}>
            <Button
              variant="primary"
              size="medium"
              disabled={loading}
              onClick={() => provision()}
              data-testid={`${dataTestId}__confirm-provision`}
            >
              {loading ? 'Creating…' : 'Yes, create it'}
            </Button>
            <Button
              variant="secondary"
              size="medium"
              disabled={loading}
              onClick={() => setIsConfirming(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            variant="primary"
            size="medium"
            onClick={() => setIsConfirming(true)}
            data-testid={`${dataTestId}__provision`}
          >
            Create workspace
          </Button>
        </div>
      )}
    </div>
  );
}
